import { Node } from 'reactflow';

import { JSONSchema7, JSONSchema7Definition } from 'json-schema';

import { JSONSchema } from '../types';
import { sourceDataObject } from './SchemaRow/state';

export const getSchemaWithSources = (schema: JSONSchema, nodeId: string = "") => {
  const addSourcesToSchema = (properties: { [key: string]: any }, path = '') => {
    return Object.keys(properties).reduce<{ [key: string]: any }>((acc, key) => {
      const currentPath = path ? `${path}.${key}` : key;
      const property = properties[key];
      const source = sourceDataObject[`${nodeId}.${currentPath}`];

      acc[key] = {
        ...property,
        ...(property.type !== 'object' ? { source: source || null } : {})
      };

      if (property.type === 'object') {
        acc[key].properties = addSourcesToSchema(property.properties, currentPath);
      }
      return acc;
    }, {});
  };

  // Assuming schema.properties exists and is an object
  return schema.properties ? { ...schema, properties: addSourcesToSchema(schema.properties) } : {};
};


export const updateSourceDataObject = (nodes: Node[]) => {
  console.log('-------updating source data object---------')

  const traverseSchema = (schema: JSONSchema7Definition, nodeId: string, parentPath: string = "") => {
    for (const [key, value] of Object.entries(schema)) {
      const currentPath = parentPath ? `${parentPath}.${key}` : key;

      // If the property has nested properties, recursively traverse them
      if (typeof value === "object" && value !== null && value.type === "object" && value.properties) {
        traverseSchema(value.properties, nodeId, currentPath);
      }

      // If the property has a `source`, update the sourceDataObject
      if (typeof value === "object" && value !== null && "source" in value) {
        sourceDataObject[`${nodeId}.${currentPath}`] = value.source;
      }

    }
  };

  nodes.forEach(node => {
    const excludeNodeTypes = ["requestNode", "placeholder"];

    if (!excludeNodeTypes.includes(node.type || '') && node.data.nodeConfig?.schema) {
      traverseSchema(node.data.nodeConfig.schema.properties, node.id);
    }
  })
};


export const processSourceKeys = (properties: any, action: 'validate' | 'remove'): boolean | undefined => {
  for (const key in properties) {
    const property = properties[key];

    // Check if it's an object type and traverse its nested properties
    if (property.type === "object" && property.properties) {
      // Recursive call for nested properties
      const result = processSourceKeys(property.properties, action);

      if (action === 'validate' && !result) return false;
    }
    else {
      if (action === 'validate') {
        // Validate: Check for 'source' key existence and non-empty value
        // if (!("source" in property) || property.source === "" || property.source === undefined) {
        //   return false;
        // }

        // For non-object types, check if 'source' key is present irrespective of value
        if (!property.hasOwnProperty('source')) {
          return false;
        }
      }
      else if (action === 'remove') {
        // Remove: Delete 'source' key if it exists
        if ("source" in property) {
          delete property.source;
        }
      }
    }
  }
  return action === 'validate' ? true : undefined;
}


export const addPropertiesToRootSchema = (
  schema: JSONSchema7,
  sourceData: Record<string, string>,
  parentPath = "",
  excludeTypes = ["object", "array"]
): JSONSchema7 => {
  // Create a deep copy of the schema to avoid mutating the original
  const filteredSchema: JSONSchema7 = { ...schema };

  // Helper function to check if a sourceData key matches the current path
  const isMatchingPath = (currentPath: string) => {
    for (const [sourceKey, sourceValue] of Object.entries(sourceData)) {
      const sourcePath = sourceKey.split('.').slice(1).join('.'); // Remove the ID part (first segment)
      if (sourcePath === currentPath && sourceValue === `$root.${currentPath}`) {
        return true;
      }
    }
    return false;
  };

  const isPresentInSourceData = (currentPath: string) => {
    const sourceKeys = Object.keys(sourceData).map(key => key.split('.').slice(1).join('.'));

    return !sourceKeys.includes(currentPath);
  }

  // Update properties
  if (filteredSchema.properties) {
    const filteredProperties: Record<string, JSONSchema7Definition> = {};

    for (const [key, value] of Object.entries(filteredSchema.properties)) {
      // Construct the current path for this property
      const currentPath = parentPath ? `${parentPath}.${key}` : key;

      // Check if the current path matches with sourceData and includes "root"
      const condition = excludeTypes.includes((value as JSONSchema7).type as string || '');
      const isPresentSourceData = condition ? false : isPresentInSourceData(currentPath);
      const hasRoot = isMatchingPath(currentPath);

      if (isPresentSourceData || hasRoot) {
        // If 'root' is found, include this property
        filteredProperties[key] = value;
      }
      else if (typeof value === "object" && (value.type === "object" || value.type === "array")) {
        // If it's a nested object, continue filtering recursively
        const nestedFilteredSchema = addPropertiesToRootSchema(
          (value.type === 'array' ? value.items as JSONSchema7 : value as JSONSchema7),
          sourceData,
          currentPath,
          excludeTypes
        );

        // Only add the nested object if it has properties to include
        if (nestedFilteredSchema.properties && Object.keys(nestedFilteredSchema.properties).length > 0) {
          filteredProperties[key] = value.type === 'array' ? { type: 'array', items: nestedFilteredSchema } : nestedFilteredSchema;
        }
      }
    }

    filteredSchema.properties = filteredProperties;
  }

  if (filteredSchema.required) {
    filteredSchema.required = filteredSchema.required.filter(
      (key) => filteredSchema.properties && key in filteredSchema.properties
    );
  }

  return filteredSchema;
}


export const buildPathsFromSchema = (
  schema: JSONSchema7,
  sourceData: Record<string, string>,
  nodeId: string,
  savedPaths = new Set<string>(),
  parentPath = "",
) => {

  if (schema.properties) {
    for (const [key, value] of Object.entries(schema.properties)) {
      const currentPath = parentPath ? `${parentPath}.${key}` : key;

      if (typeof value === "object" && (value.type === "object" || value.type === "array")) {
        buildPathsFromSchema(
          (value.type === 'array' ? value.items as JSONSchema7 : value),
          sourceData,
          nodeId,
          savedPaths,
          currentPath,
        );
      }
      else {
        savedPaths.add(currentPath);
        delete sourceData[`${nodeId}.${currentPath}`];
      }
    }
  }

  return savedPaths;
}


export const filterPropertiesFromSchema = (schema: JSONSchema7, savedPaths: Set<string>, parentPath = ''): JSONSchema7 => {

  if (!schema?.properties) return schema;

  const filteredProperties: Record<string, JSONSchema7Definition> = {};
  const updatedRequired: string[] = [];

  for (const key in schema.properties) {
    const value = schema.properties[key] as JSONSchema7;
    const currentPath = parentPath ? `${parentPath}.${key}` : key;

    if (!savedPaths.has(currentPath)) {
      if (typeof value === "object" && (value.type === "object" || value.type === "array")) {
        const filteredValue = filterPropertiesFromSchema((value.type === 'array' ? value.items as JSONSchema7 : value), savedPaths, currentPath);

        if (filteredValue.properties && Object.keys(filteredValue.properties).length > 0) {
          filteredProperties[key] = value.type === 'array' ? { ...value, items: filteredValue } : filteredValue;

          if (schema?.required?.includes(key)) {
            updatedRequired.push(key);
          }
        }
      }
      else {
        filteredProperties[key] = value;

        if (schema?.required?.includes(key)) {
          updatedRequired.push(key);
        }
      }
    }
  }

  return {
    ...schema,
    properties: filteredProperties,
    required: updatedRequired
  };
}